import React from "react";

/**
 * ErrorBoundary — apanha erros de render em componentes descendentes e mostra
 * um fallback amigável em vez de deixar o React desmontar a árvore (o que
 * resultava num ecrã preto porque o body tem bg-background dark).
 *
 * Reseta automaticamente quando a `location` (pathname) muda, para permitir
 * recuperar após navegação sem exigir F5.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary] Uncaught render error:", error, info);
  }

  componentDidUpdate(prevProps) {
    // Ao mudar de rota, tentar renderizar novamente
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const fallback = this.props.fallback;
      if (typeof fallback === "function") {
        return fallback({ error: this.state.error, reset: this.handleReset });
      }
      return (
        <div
          data-testid="error-boundary-fallback"
          className="flex min-h-screen flex-col items-center justify-center gap-6 bg-background px-6 py-24 text-center"
        >
          <p className="text-[10px] uppercase tracking-[0.45em] text-gold">Magnolia Lunar</p>
          <h1 className="font-display text-3xl font-light text-foreground sm:text-4xl">
            Algo correu mal
          </h1>
          <p className="max-w-md text-sm text-foreground/72">
            Ocorreu um problema inesperado ao carregar esta página. Pode tentar novamente
            ou voltar ao início.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReset}
              className="rounded-full bg-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-black transition-colors hover:bg-gold-light"
            >
              Tentar novamente
            </button>
            <a
              href="/"
              className="rounded-full border border-foreground/25 px-6 py-3 text-xs uppercase tracking-[0.2em] text-foreground transition-colors hover:border-gold hover:text-gold"
            >
              Voltar ao início
            </a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
